import { API_ENDPOINTS } from "./endpoints";

export const KPI_ENDPOINT_MAP: Record<number, string> = {
  1: API_ENDPOINTS.HOME.FINANCIAL_KPI_ONE,
  2: API_ENDPOINTS.HOME.FINANCIAL_KPI_TWO,
  3: API_ENDPOINTS.HOME.FINANCIAL_KPI_THREE,
  4: API_ENDPOINTS.HOME.FINANCIAL_KPI_FOUR,
  5: API_ENDPOINTS.HOME.FINANCIAL_KPI_FIVE,
  6: API_ENDPOINTS.HOME.TECHNICAL_KPI_ONE,
  7: API_ENDPOINTS.HOME.TECHNICAL_KPI_TWO,
  8: API_ENDPOINTS.HOME.TECHNICAL_KPI_THREE,
  9: API_ENDPOINTS.HOME.CAPACITY_KPI_ONE,
  10: API_ENDPOINTS.HOME.CAPACITY_KPI_TWO,
  11: API_ENDPOINTS.HOME.CAPACITY_KPI_THREE,
  12: API_ENDPOINTS.HOME.FINANCIAL_KPI_SIX,
  13: API_ENDPOINTS.HOME.FINANCIAL_KPI_SEVEN,
};

export type DropdownField = {
  key: string;
  label: string;
  multiSelect?: boolean;
  filterName: string;
  defaultValue?: string;
  isIndependent: boolean;
};

export type KpiConfig = {
  component: "bar" | "line";
  stacked?: boolean;
  yAxisLabel: string;
  xAxisDataKey: string;
  isFinancialKpi: boolean;
  fields: DropdownField[];
  componentMapping?: Record<string, string>;
};

export const KPI_CONFIG: Record<number, KpiConfig> = {
  1: {
    component: "bar",
    stacked: true,
    yAxisLabel: "Million USD",
    xAxisDataKey: "year",
    isFinancialKpi: true,
    componentMapping: {
      "Type of support": "stackedBar",
      "Financial instrument": "bar",
      Sector: "bar",
      Channel: "bar",
      Status: "stackedBar",
      "Funding source": "stackedBar",
      Recipient: "horizontalBar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "fromYear",
        label: "From (Year)",
        filterName: "years",
        isIndependent: true,
      },
      {
        key: "toYear",
        label: "To (Year)",
        filterName: "years",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Financial support provided (bilateral, regional, others)
  2: {
    component: "line",
    yAxisLabel: "Million USD",
    xAxisDataKey: "year",
    isFinancialKpi: true,
    componentMapping: {
      "Amount purpose": "line",
      Recipient: "horizontalBar",
      Status: "bar",
      "Type of support": "stackedBar",
      Purpose: "bar",
      "Financial instrument": "bar",
      Sector: "bar",
      Channel: "bar",
      "Funding source": "stackedBar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Purpose",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "fromYear",
        label: "From (Year)",
        filterName: "years",
        isIndependent: true,
      },
      {
        key: "toYear",
        label: "To (Year)",
        filterName: "years",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Financial support provided (multilateral)
  3: {
    component: "bar",
    yAxisLabel: "Million USD",
    xAxisDataKey: "year",
    isFinancialKpi: true,
    componentMapping: {
      "Amount Mobilized": "bar",
      "Type of public Intervention": "bar",
      "Type of support": "stackedBar",
      Sector: "bar",
      Channel: "bar",
      Recipient: "horizontalBar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "fromYear",
        label: "From (Year)",
        filterName: "years",
        isIndependent: true,
      },
      {
        key: "toYear",
        label: "To (Year)",
        filterName: "years",
        isIndependent: true,
      },
      // {
      //   key: "regions",
      //   label: "Regions",
      //   multiSelect: true,
      //   filterName: "regions",
      //   isIndependent: true,
      // },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Financial support mobilized
  4: {
    component: "bar",
    stacked: true,
    yAxisLabel: "Million USD",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: true,
    componentMapping: {
      "Type of support": "stackedBar",
      Sector: "bar",
      "Financial instrument": "bar",
      Contribution: "bar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btrcycle",
        label: "BTR",
        multiSelect: true,
        filterName: "btrcycle",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Financial support needed
  5: {
    component: "bar",
    stacked: true,
    yAxisLabel: "Million USD",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: true,
    componentMapping: {
      "Type of support": "stackedBar",
      "Financial instrument": "bar",
      Sector: "bar",
      Contribution: "bar",
      "Status of activity": "bar",
      Channel: "bar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btrcycle",
        label: "BTR",
        multiSelect: true,
        filterName: "btrcycle",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Financial support received
  6: {
    component: "bar",
    stacked: true,
    yAxisLabel: "Projects, programs, activity or other",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: false,
    componentMapping: {
      Sector: "bar",
      "Type of support": "stackedBar",
      Status: "bar",
      "Activity undertaken by": "bar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btr",
        label: "BTR",
        multiSelect: true,
        filterName: "btr",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Tech transfer support provided
  7: {
    component: "bar",
    yAxisLabel: "Projects, programs, activity or other",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: false,
    componentMapping: {
      "Type of support": "stackedBar",
      Sector: "bar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btr",
        label: "BTR",
        multiSelect: true,
        filterName: "btr",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Tech transfer support needed
  8: {
    component: "bar",
    yAxisLabel: "Projects, programs, activity or other",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: false,
    componentMapping: {
      "Type of support": "stackedBar",
      Sector: "bar",
      "Status of Activity": "bar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btr",
        label: "BTR",
        multiSelect: true,
        filterName: "btr",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Tech transfer support received
  9: {
    component: "bar",
    stacked: true,
    yAxisLabel: "Projects, programs, activity or other",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: false,
    componentMapping: {
      Status: "bar",
      "Type of support": "stackedBar",
      Recipient: "stackedBar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btrcycle",
        label: "BTR",
        multiSelect: true,
        filterName: "btrcycle",
        isIndependent: true,
      },
      // {
      //   key: "fromYear",
      //   label: "Year",
      //   filterName: "years",
      //   isIndependent: true,
      // },
      // { key: "toYear", label: "To", filterName: "years", isIndependent: true },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Capacity-building provided
  10: {
    component: "bar",
    stacked: true,
    yAxisLabel: "Projects, programs, activity or other",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: false,
    componentMapping: {
      "Type of support": "stackedBar",
      Sector: "bar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btrcycle",
        label: "BTR",
        multiSelect: true,
        filterName: "btrcycle",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Capacity-building needed
  11: {
    component: "bar",
    yAxisLabel: "Projects, programs, activity or other",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: false,
    componentMapping: {
      "Type of support": "stackedBar",
      Sector: "bar",
      "Status of Activity": "bar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Type of support",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btrcycle",
        label: "BTR",
        multiSelect: true,
        filterName: "btrcycle",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Capacity-building received
  12: {
    component: "bar",
    yAxisLabel: "Million USD",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: true,
    componentMapping: {
      Channel: "bar",
      "Status of Activity": "bar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Channel",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btrcycle",
        label: "BTR",
        multiSelect: true,
        filterName: "btrcycle",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Article 13 support needed
  13: {
    component: "bar",
    yAxisLabel: "Million USD",
    xAxisDataKey: "btrcycle",
    isFinancialKpi: true,
    componentMapping: {
      Channel: "bar",
      "Status of Activity": "bar",
    },
    fields: [
      {
        key: "viewBy",
        label: "View By",
        filterName: "viewBy",
        defaultValue: "Channel",
        isIndependent: true,
      },
      {
        key: "secondaryFilter",
        label: "Secondary Filter",
        multiSelect: true,
        filterName: "secondaryFilter",
        isIndependent: false,
      },
      {
        key: "btrcycle",
        label: "BTR",
        multiSelect: true,
        filterName: "btrcycle",
        isIndependent: true,
      },
      {
        key: "party",
        label: "Party",
        multiSelect: true,
        filterName: "party",
        isIndependent: true,
      },
    ],
  }, // Article 13 support received
};

export const DEFAULT_KPI_CONFIG = {
  component: "bar" as const,
  yAxisLabel: "",
  xAxisDataKey: "year",
  isFinancialKpi: false,
  fields: [] as DropdownField[],
};

export const KPI_CATEGORY_MAPPER: Record<number, string> = {
  1: "Financial support",
  2: "Financial support",
  3: "Financial support",
  4: "Financial support",
  5: "Financial support",
  12: "Financial support",
  13: "Financial support",
  6: "Technology development and transfer support",
  7: "Technology development and transfer support",
  8: "Technology development and transfer support",
  9: "Capacity-building support",
  10: "Capacity-building support",
  11: "Capacity-building support",
};

export const FTC_KPI_GRAPH_ENDPOINT_MAP: Record<number, string> = {
  1: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_ONE_GRAPH,
  2: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_TWO_GRAPH, //Financial support (multilateral channels)
  3: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_THREE_GRAPH, //Financial support mobilized
  4: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FOUR_GRAPH, //Finance support needed
  5: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FIVE_GRAPH, //Finance support received
  6: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_ONE_GRAPH, //Technology support provided
  7: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_SECOND_GRAPH, //Technology support needed
  9: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_ONE_GRAPH, //Capacity building support provided
  10: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_SECOND_GRAPH, //Capacity building support needed
  11: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_THREE_GRAPH, //Capacity building support received
  8: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_THREE_GRAPH,
  12: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SIX_GRAPH, // Implementation Article 13 support needed
  13: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SEVEN_GRAPH, // Implementation Article 13 support received
};

export const FTC_KPI_GRAPH_FILTER_ENDPOINT_MAP: Record<number, string> = {
  1: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_ONE_FILTER,
  2: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_TWO_FILTER, //Financial support (multilateral channels)
  3: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_THREE_FILTER, //Financial support mobilized
  4: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FOUR_FILTER, //Finance support needed
  5: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FIVE_FILTER, //Finance support received
  6: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_ONE_FILTER, //Technology support provided
  7: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_SECOND_FILTER, //Technology support needed
  9: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_ONE_FILTER, //Capacity building support provided
  10: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_SECOND_FILTER, //Capacity building support needed
  11: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_THREE_FILTER, //Capacity building support received
  8: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_THREE_FILTER,
  12: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SIX_FILTER, // Implementation Article 13 support needed
  13: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SEVEN_FILTER, // Implementation Article 13 support received
};

export const FTC_KPI_FACTOIDS_ENDPOINT_MAP: Record<number, string> = {
  1: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_ONE_FACTOID,
  2: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_TWO_FACTOID, //Financial support (multilateral channels)
  3: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_THREE_FACTOID, //Financial support mobilized
  4: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FOUR_FACTOID, //Finance support needed
  5: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FIVE_FACTOID, //Finance support received
  6: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_ONE_FACTOID, //Technology support provided
  7: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_SECOND_FACTOID, //Technology support needed
  9: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_ONE_FACTOID, //Capacity building support provided
  10: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_SECOND_FACTOID, //Capacity building support needed
  11: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_THREE_FACTOID, //Capacity building support received
  8: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_THREE_FACTOID,
  12: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SIX_FACTOID, // Implementation Article 13 support needed
  13: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SEVEN_FACTOID, // Implementation Article 13 support received
};

export const FTC_KPI_TABLE_ENDPOINT_MAP: Record<number, string> = {
  1: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_ONE_TABLE,
  2: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_TWO_TABLE, //Financial support (multilateral channels)
  3: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_THREE_TABLE, //Financial support mobilized
  4: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FOUR_TABLE, //Finance support needed
  5: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FIVE_TABLE, //Finance support received
  6: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_ONE_TABLE, //Technology support provided
  7: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_SECOND_TABLE, //Technology support needed
  9: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_ONE_TABLE, //Capacity building support provided
  10: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_SECOND_TABLE, //Capacity building support needed
  11: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_THREE_TABLE, //Capacity building support received
  8: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_THREE_TABLE,
  12: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SIX_TABLE, // Implementation Article 13 support needed
  13: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SEVEN_TABLE, // Implementation Article 13 support received
};

export const FTC_KPI_TABLE_FILTER_ENDPOINT_MAP: Record<number, string> = {
  1: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_ONE_TABLE_FILTER,
  2: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_TWO_TABLE_FILTER, //Financial support (multilateral channels)
  3: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_THREE_TABLE_FILTER, //Financial support mobilized
  4: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FOUR_TABLE_FILTER, //Finance support needed
  5: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FIVE_TABLE_FILTER, //Finance support received
  6: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_ONE_TABLE_FILTER, //Technology support provided
  7: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_SECOND_TABLE_FILTER, //Technology support needed
  9: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_ONE_TABLE_FILTER, //Capacity building support provided
  10: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_SECOND_TABLE_FILTER, //Capacity building support needed
  11: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_THREE_TABLE_FILTER, //Capacity building support received
  8: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_THREE_TABLE_FILTER,
  12: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SIX_TABLE_FILTER, // Implementation Article 13 support needed
  13: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SEVEN_TABLE_FILTER, // Implementation Article 13 support received
};

export const FTC_KPI_ANALYSIS_ENDPOINT_MAP: Record<number, string> = {
  1: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_ONE_ANALYSIS,
  2: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_TWO_ANALYSIS,
  3: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_THREE_ANALYSIS,
  4: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FOUR_ANALYSIS,
  5: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_FIVE_ANALYSIS,
  9: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_ONE_ANALYSIS,
  10: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_SECOND_ANALYSIS,
  11: API_ENDPOINTS.CAPACITY.CAPACITY_KPI_THREE_ANALYSIS,
  6: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_ONE_ANALYSIS,
  7: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_SECOND_ANALYSIS,
  8: API_ENDPOINTS.TECHNOLOGY.TECHNOLOGY_KPI_THREE_ANALYSIS,
  12: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SIX_ANALYSIS,
  13: API_ENDPOINTS.FINANCIAL.FINANCIAL_KPI_SEVEN_ANALYSIS,
};

export const KPI_FACTOID_LABELS: Record<number, string[]> = {
  1: [
    "Total amount (face value)",
    "Total amount (face value) for adaptation",
    "Total amount (face value) for mitigation",
    "Total amount (face value) for cross-cutting",
    "Number of projects, programmes, activities supported",
  ],
  2: [
    "Total inflow amount (face value) for core/general",
    "Total inflow amount (face value) for climate-specific",
    "Total inflow amount (face value) for mitigation",
    "Total inflow amount(face value) for cross-cutting",
    "Number of projects, programmes, activities supported",
  ],
  3: [
    "Total amount mobilized (face value)",
    "Total amount used to mobilize the support (face value)",
    "Total amount mobilized (face value) for mitigation",
    "Total amount mobilized (face value) for adaptation",
    "Total amount mobilized (face value) for cross-cutting",
    "Number of projects, programmes, activities supported",
  ],
  4: [
    "Total amount",
    "Total amount for adaptation",
    "Total amount for mitigation",
    "Total amount for cross-cutting",
    "Number of projects, programmes, activities",
  ],
  5: [
    "Total amount",
    "Total amount for adaptation",
    "Total amount for mitigation",
    "Total amount for cross-cutting",
    "Number of projects, programmes, activities",
  ],
  6: [
    "Number of projects, programmes, activities supported",
    "Number of projects, programmes, activities undertaken by public sector",
    "Number of projects, programmes, activities undertaken by private sector",
    "Number of projects, programmes, activities undertaken by public and private sector",
  ],
  7: [
    "Number of projects, programmes, activities",
    "Number of projects, programmes, activities for mitigation",
    "Number of projects, programmes, activities for adaptation",
    "Number of projects, programmes, activities for cross-cutting",
  ],
  8: [
    "Number of projects, programmes, activities",
    "Number of projects, programmes, activities for mitigation",
    "Number of projects, programmes, activities for adaptation",
    "Number of projects, programmes, activities for cross-cutting",
  ],
  9: [
    "Number of projects, programmes, activities supported",
    "Number of projects, programmes, activities supported for mitigation",
    "Number of projects, programmes, activities supported for adaptation",
    "Number of projects, programmes, activities supported for cross-cutting",
  ],
  10: [
    "Number of projects, programmes, activities",
    "Number of projects, programmes, activities for mitigation",
    "Number of projects, programmes, activities for adaptation",
    "Number of projects, programmes, activities for cross-cutting",
  ],
  11: [
    "Number of projects, programmes, activities",
    "Number of projects, programmes, activities for mitigation",
    "Number of projects, programmes, activities for adaptation",
    "Number of projects, programmes, activities for cross-cutting",
  ],
  12: [
    "Total amount",
    "Total amount for projects, programmes, activities in planned status",
    "Total amount for projects, programmes, activities in ongoing status",
    "Total amount for projects, programmes, activities in completed status",
  ],
  13: [
    "Total amount",
    "Total amount for projects, programmes, activities in planned status",
    "Total amount for projects, programmes, activities in ongoing status",
    "Total amount for projects, programmes, activities in completed status",
  ],
};
